/**
 * The fetch wrapper every API call goes through: same-origin, cookie
 * credentials, JSON, the CSRF header, and one error shape.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const CSRF_COOKIE = 'ef_csrf';

function readCookie(name: string): string {
  const found = document.cookie.split('; ').find((c) => c.startsWith(name + '='));
  return found ? decodeURIComponent(found.slice(name.length + 1)) : '';
}

export type Query = Record<string, string | number | boolean | null | undefined>;

interface Options {
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
  raw?: boolean;
}

export function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

async function toApiError(res: Response): Promise<ApiError> {
  let payload: { error?: { code?: string; message?: string; fields?: Record<string, string> } } | null = null;
  try {
    payload = await res.json();
  } catch {
    // Not JSON.
  }
  const e = payload?.error ?? {};
  return new ApiError(res.status, e.code ?? `HTTP_${res.status}`, e.message ?? res.statusText ?? 'Request failed', e.fields);
}

export async function http<T = unknown>(method: string, path: string, opts: Options = {}): Promise<T> {
  const url = withQuery(path, opts.query);
  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) body = opts.body;
  else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  if (method !== 'GET' && method !== 'HEAD') headers['X-CSRF-Token'] = readCookie(CSRF_COOKIE);

  let res: Response;
  try {
    res = await fetch(url, { method, headers, body, credentials: 'include', signal: opts.signal });
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    throw new ApiError(0, 'NETWORK', 'Could not reach the server. Check your connection and try again.');
  }

  if (res.status === 401 && !path.startsWith('/api/auth/')) {
    window.dispatchEvent(new Event('auth:unauthorized'));
  }
  // A fresh CSRF cookie was just issued on a mutating request; retry once.
  if (res.status === 403 && method !== 'GET') {
    const clone = res.clone();
    const payload = await clone.json().catch(() => null);
    if (payload?.error?.code === 'CSRF' && readCookie(CSRF_COOKIE)) {
      headers['X-CSRF-Token'] = readCookie(CSRF_COOKIE);
      res = await fetch(url, { method, headers, body, credentials: 'include', signal: opts.signal });
    }
  }
  if (opts.raw) {
    if (!res.ok) throw await toApiError(res);
    return res as unknown as T;
  }
  if (res.status === 204) return undefined as T;
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as T;
}

export const get = <T>(path: string, query?: Query) => http<T>('GET', path, { query });
export const post = <T>(path: string, body?: unknown) => http<T>('POST', path, { body });
export const put = <T>(path: string, body?: unknown) => http<T>('PUT', path, { body });
export const patch = <T>(path: string, body?: unknown) => http<T>('PATCH', path, { body });
export const del = <T>(path: string) => http<T>('DELETE', path);
export const getBlob = (path: string, query?: Query) => http<Response>('GET', path, { query, raw: true }).then((r) => r.blob());
